/**
 * Creative review queue — the ordered walk an operator takes through a batch.
 *
 * Reviewing nine Never Stop Chasing videos meant nine separate modal sessions:
 * open, wait for a preview, approve, close, scroll, find the next row, reopen,
 * wait again. The waiting was a source-master download (288-751MB per asset);
 * the rest was navigation the product never provided.
 *
 * This module owns the queue decisions — order, position, advancing, keyboard
 * intent, and the guards that stop one asset's state leaking into the next — as
 * plain functions, so they are testable without mounting Creative Studio and
 * identical in Cortex and Quantum.
 *
 * It deliberately does NOT decide anything governed. It never approves, never
 * infers an approval from a neighbouring asset, and never reorders around
 * approval state. Advancing is navigation; approving is a separate, explicit,
 * per-asset action that the caller performs and confirms first.
 *
 * This file must stay byte-identical between the client and superadmin apps.
 */

export const REVIEW_QUEUE_ACTIONS = Object.freeze({
  PREVIOUS: "previous",
  NEXT: "next",
  CLOSE: "close",
});

const asId = (value) => {
  const id = value && typeof value === "object" ? value.id : value;
  const text = String(id ?? "").trim();
  return text || null;
};

/**
 * Build the queue from the rows the operator can currently SEE.
 *
 * The queue is the visible, already-filtered, already-ordered list — never a
 * fresh query. That is what keeps it scoped to the current advertiser, campaign
 * and status filter without this module knowing what any of those are, and what
 * stops an unrelated creative appearing mid-review.
 */
export function buildReviewQueue(rows = []) {
  const seen = new Set();
  const ids = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const id = asId(row);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return { ids, total: ids.length };
}

export function reviewQueueIndexOf(queue, assetId) {
  const id = asId(assetId);
  if (!id) return -1;
  return (queue?.ids || []).indexOf(id);
}

/**
 * Where the reviewer is, in the words the UI shows. `position` is 1-based
 * because "Creative 0 of 9" is not a thing an operator should ever read.
 */
export function reviewQueuePosition(queue, assetId) {
  const index = reviewQueueIndexOf(queue, assetId);
  const total = queue?.total || 0;
  if (index < 0) {
    return { index: -1, position: 0, total, label: "", remaining: 0, hasPrevious: false, hasNext: false, isLast: false };
  }
  const position = index + 1;
  return {
    index,
    position,
    total,
    label: `Creative ${position} of ${total}`,
    remaining: Math.max(0, total - position),
    hasPrevious: index > 0,
    hasNext: index < total - 1,
    isLast: index === total - 1,
  };
}

export function reviewQueueNeighborId(queue, assetId, direction) {
  const index = reviewQueueIndexOf(queue, assetId);
  if (index < 0) return null;
  const step = direction === REVIEW_QUEUE_ACTIONS.PREVIOUS ? -1 : 1;
  const target = index + step;
  const ids = queue?.ids || [];
  if (target < 0 || target >= ids.length) return null;
  return ids[target];
}

/**
 * Where an "…& Next" action lands.
 *
 * Advancing is positional, not approval-driven: it goes to the NEXT asset in
 * the queue, whatever its state. Skipping over assets the operator has not
 * acted on would silently drop them from the review, and re-sorting mid-walk
 * would move the ground under someone halfway through a batch. A skipped asset
 * keeps its pending state and is simply still there.
 *
 * On the last asset there is nowhere to advance to, which is what makes the
 * action read "Approve & Finish" rather than "Approve & Next".
 */
export function resolveAdvanceTarget(queue, assetId) {
  const position = reviewQueuePosition(queue, assetId);
  if (position.index < 0) return { nextAssetId: null, isFinish: true, position };
  if (position.isLast) return { nextAssetId: null, isFinish: true, position };
  return {
    nextAssetId: reviewQueueNeighborId(queue, assetId, REVIEW_QUEUE_ACTIONS.NEXT),
    isFinish: false,
    position,
  };
}

/**
 * Action label for a governed decision, so the operator is told what the button
 * will do to their place in the queue as well as to the creative.
 */
export function reviewActionLabel(baseLabel, { isFinish } = {}) {
  const label = String(baseLabel || "").trim();
  if (!label) return "";
  return isFinish ? `${label} & Finish` : `${label} & Next`;
}

const TEXT_ENTRY_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/**
 * Is the keyboard currently being used to WRITE rather than to navigate?
 *
 * Arrow keys inside a comment box move the caret. Stealing them to change asset
 * would lose the comment and jump the operator somewhere unexpected, so text
 * entry and anything explicitly editable always wins.
 */
export function isTextEntryTarget(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = String(target.tagName || "").toUpperCase();
  if (TEXT_ENTRY_TAGS.has(tag)) return true;
  if (typeof target.closest === "function") {
    return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
  }
  return false;
}

/**
 * Translate a keystroke into a queue intent, or null to leave it alone.
 *
 * Escape is honoured even inside a text field: a reviewer who wants out must
 * always get out, and being unable to close is the failure this whole change
 * exists to prevent. Modified keystrokes are never claimed — those belong to
 * the browser and to assistive technology.
 */
export function resolveReviewKeyAction(event) {
  if (!event) return null;
  if (event.altKey || event.ctrlKey || event.metaKey) return null;
  const key = String(event.key || "");
  if (key === "Escape") return REVIEW_QUEUE_ACTIONS.CLOSE;
  if (isTextEntryTarget(event.target)) return null;
  if (event.shiftKey) return null;
  if (key === "ArrowLeft") return REVIEW_QUEUE_ACTIONS.PREVIOUS;
  if (key === "ArrowRight") return REVIEW_QUEUE_ACTIONS.NEXT;
  return null;
}

/**
 * Would leaving this asset discard something the operator typed?
 *
 * Only unsaved text counts. Whitespace is not work, and an unchanged draft that
 * merely differs by trailing spaces must not nag someone through nine assets.
 */
export function hasUnsavedReviewComment(draft, saved = "") {
  return String(draft || "").trim() !== String(saved || "").trim();
}

export function shouldWarnBeforeLeavingAsset({ draftComment, savedComment, isSubmitting } = {}) {
  if (isSubmitting) return true;
  return hasUnsavedReviewComment(draftComment, savedComment);
}

/**
 * The per-asset state that must be thrown away when the queue moves.
 *
 * Everything here is a fact about the ASSET the operator just left. Carrying
 * any of it forward would show one creative's preview, error, or comment
 * against another's record — the specific way a queue lies to a reviewer.
 */
export function resetPerAssetReviewState() {
  return {
    previewState: "idle",
    previewError: null,
    posterLoaded: false,
    draftComment: "",
    submitError: null,
    scrollTop: 0,
  };
}

/**
 * What to prefetch while the reviewer looks at the current asset.
 *
 * Exactly one asset ahead, metadata and poster only. The masters in this
 * library run to 751MB; prefetching even one of them would reintroduce the
 * stall this change removes, so source media is never a prefetch target and
 * the depth is fixed rather than configurable.
 */
export function resolveReviewPrefetch(queue, assetId) {
  const nextAssetId = reviewQueueNeighborId(queue, assetId, REVIEW_QUEUE_ACTIONS.NEXT);
  if (!nextAssetId) return { assetIds: [], includesSourceMedia: false };
  return { assetIds: [nextAssetId], includesSourceMedia: false };
}

/**
 * Non-binding context: creatives in the queue sharing one governance-relevant
 * profile. Surfaced so a reviewer walking four identical 9:16 ProRes masters
 * knows why they look alike — it never groups an approval, and every asset
 * still requires its own explicit action.
 */
export function summarizeSharedReviewProfiles(assets = []) {
  const groups = new Map();
  for (const asset of Array.isArray(assets) ? assets : []) {
    const width = asset?.width ?? asset?.studioMeta?.width ?? null;
    const height = asset?.height ?? asset?.studioMeta?.height ?? null;
    const container = String(asset?.extension || asset?.container || "").toLowerCase();
    const codec = String(asset?.codec || asset?.videoCodec || "");
    if (!width || !height) continue;
    const key = `${width}x${height}|${codec}|${container}`;
    if (!groups.has(key)) {
      groups.set(key, { key, width, height, codec: codec || null, container: container || null, count: 0 });
    }
    groups.get(key).count += 1;
  }
  return Array.from(groups.values())
    .filter((group) => group.count > 1)
    .sort((left, right) => right.count - left.count)
    .map((group) => ({
      ...group,
      label: `${group.count} creatives share the same ${group.width}×${group.height}${
        group.codec ? ` ${group.codec}` : ""
      }${group.container ? ` ${group.container.toUpperCase()}` : ""} profile.`,
    }));
}
